import { Card, Pill } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { CardActionArea, CardContent, Stack, type Theme } from "@mui/material";
import { BaseLink } from "@src/appV2/lib";
import { calculateMissingCredential } from "@src/appV2/redesign/Documents/utils/calculateMissingCredential";
import { getPlacementDetailsPath } from "@src/appV2/redesign/Placements/paths";
import { getCardType } from "@src/appV2/redesign/Placements/utils/card-type";
import { useInView } from "react-intersection-observer";

import { PLACEMENT_STATUS_MAP } from "../../types/fetchPlacements.schema";
import { PlacementCardBody } from "./components/PlacementCardBody";
import { PlacementCardFooter } from "./components/PlacementCardFooter";
import { PlacementCardHeader } from "./components/PlacementCardHeader";
import { PlacementVerifiedTypePill } from "./components/PlacementVerifiedTypePill";
import { type PlacementCardPropsV2 } from "./types";

export function PlacementCardV2(props: PlacementCardPropsV2) {
  const {
    placement,
    placementCandidateId,
    isRestricted,
    loadingPayData,
    minMaxPayEstimate,
    workerQualifications,
    source,
    onInView,
    onFooterInView,
    onClick,
    ongoingFavoritingRequest,
    onFavoriteToggle,
  } = props;

  const { ref } = useInView({
    triggerOnce: true,
    threshold: 0,
    onChange: (inView) => {
      if (!inView) {
        return;
      }

      if (isDefined(onInView)) {
        onInView();
      }
    },
  });

  const {
    id: placementId,
    status,
    matchedType,
    workerTypes,
    isFavorite,
    interviewSettings,
    webApplicationLink,
    allowRequestInterview,
    jobShifts,
    payRange,
    tags,
    distance,
    workerRequirements,
    workplace: {
      neighborhood: workplaceNeighborhood,
      name: workplaceName,
      photos: workplacePhotos,
    },
    isEasyScheduling,
    credentialRequirements,
  } = placement;

  const workplacePhotoUrl = source === "placement_card" ? workplacePhotos?.[0] : undefined;

  const cardType = getCardType();

  const blurPlacement =
    isRestricted && !["booked_interviews", "sent_applications"].includes(source);
  const isRecentlyAdded = matchedType === "recentlyAdded";
  const shouldDisablePlacement = status !== PLACEMENT_STATUS_MAP.OPEN;

  const getCardBorder = (theme: Theme) => {
    if (isRecentlyAdded) {
      return `1.5px solid ${theme.palette.info?.light}`;
    }

    return `1px solid ${theme.palette.border?.primary}`;
  };

  const missingCredentialsType = calculateMissingCredential(credentialRequirements ?? []);

  return (
    <Card
      ref={ref}
      variant="primary"
      sx={{
        border: (theme) => getCardBorder(theme),
        background: (theme) => theme.palette.background.quaternary,
        ...(blurPlacement ? { pointerEvents: "none" } : undefined),
        ...(shouldDisablePlacement
          ? {
              opacity: 0.5,
              filter: "grayscale(50%)",
              pointerEvents: "none",
              cursor: "not-allowed",
            }
          : undefined),
      }}
    >
      <CardActionArea
        component={BaseLink}
        to={getPlacementDetailsPath(placementId, placementCandidateId, {
          cardType,
          autoOpenInterviewBooking: isEasyScheduling,
        })}
        onClick={() => {
          if (isDefined(onClick)) {
            onClick();
          }
        }}
      >
        <CardContent>
          <Stack spacing={5}>
            <PlacementCardHeader
              isFavorite={isFavorite}
              blurPlacement={blurPlacement ?? false}
              tags={tags}
              workerRequirements={workerRequirements}
              missingCredentialsType={missingCredentialsType}
              ongoingFavoritingRequest={ongoingFavoritingRequest}
              isRecentlyAdded={isRecentlyAdded}
              placementHeaderStatus={
                shouldDisablePlacement ? (
                  <Pill
                    variant="outlined-dark"
                    size="small"
                    label="Closed"
                    backgroundColor={(theme) => theme.palette.intent?.disabled.backgroundInvert}
                    color={(theme) => theme.palette.text.secondary}
                  />
                ) : (
                  <PlacementVerifiedTypePill
                    shouldTruncate={isDefined(missingCredentialsType)}
                    interviewSettings={interviewSettings}
                    webApplicationLink={webApplicationLink}
                    allowRequestInterview={allowRequestInterview ?? false}
                  />
                )
              }
              workplacePhotoUrl={workplacePhotoUrl}
              onFavoriteToggle={onFavoriteToggle}
            />
            <PlacementCardBody
              placementId={placementId}
              jobShifts={jobShifts}
              workplaceNeighborhood={workplaceNeighborhood}
              workplaceName={workplaceName}
              distance={distance}
              blurPlacement={blurPlacement ?? false}
            />
            <PlacementCardFooter
              payRange={payRange}
              loadingPayData={loadingPayData}
              minMaxPayEstimate={minMaxPayEstimate}
              workerTypes={workerTypes}
              qualifications={workerQualifications}
              blurPlacement={blurPlacement ?? false}
              onInView={() => {
                if (isDefined(onFooterInView)) {
                  onFooterInView();
                }
              }}
            />
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
